import { NetworkStatus } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import OttoScribblesPawshake from "@assets/imgs/otto-scribbles-pawshake.svg";
import { alert } from "@components/common/Alert";
import { DSButton } from "@components/design-system/molecules";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DELETE_COMPANION_SESSION } from "@graphql/mutations/DeleteCompanionSession";
import { GET_COMPANION_SESSIONS } from "@graphql/queries/GetCompanionSessions";
import {
  AvailableCompanionTabs,
  AvailableCompanions,
  useCompanion,
} from "@hooks/use-companion";
import Twemoji from "@src/Twemoji";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useEffect, useEffectEvent, useState } from "react";

import DeleteSessionModal from "./DeleteSessionModal";

const SESSIONS_TO_LOAD = 15;

export const CompanionSessionHistory = () => {
  const { companionSession } = useCompanion();
  const { data, refetch, fetchMore, loading, networkStatus } =
    useQuery(GET_COMPANION_SESSIONS, {
      variables: { limit: SESSIONS_TO_LOAD, offset: 0 },
      notifyOnNetworkStatusChange: true,
    });
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const sessions = data?.companionSessions ?? [];
  const sessionCount = sessions.length;

  const loadMoreSessions = async () => {
    if (isFetchingMore || !hasMore || loading || sessionCount === 0) {
      return;
    }

    setIsFetchingMore(true);

    try {
      const { data: fetchMoreResult } = await fetchMore({
        variables: {
          offset: sessionCount,
          limit: SESSIONS_TO_LOAD,
        },
      });

      const returnedSessions =
        fetchMoreResult?.companionSessions?.length ?? 0;

      if (returnedSessions < SESSIONS_TO_LOAD) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to load more sessions", error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleScroll = (event) => {
    const target = event.target;
    if (!target) {
      return;
    }

    const threshold = 40;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= threshold) {
      loadMoreSessions();
    }
  };

  const onSessionChange = useEffectEvent(() => {
    refetch({ limit: SESSIONS_TO_LOAD, offset: 0 });
  });

  useEffect(() => {
    setHasMore(true);
    onSessionChange();
  }, [companionSession?.companionSessionUuid]);

  useEffect(() => {
    if (networkStatus === NetworkStatus.refetch) {
      setHasMore(true);
    }
  }, [networkStatus]);

  const handleSessionDeleted = () => {
    setHasMore(true);
  };

  return (
    <div className="flex flex-1 flex-col h-full overflow-hidden">
      <div className="overflow-y-auto flex-1" onScroll={handleScroll}>
        {sessions.length ? (
          <>
            {sessions.map((i) => (
              <CompanionSessionHistoryItem
                item={i}
                key={i.companionSessionUuid}
                onDeleted={handleSessionDeleted}
              />
            ))}
            {(isFetchingMore || loading) && (
              <div className="py-4 text-center text-xs text-gray-300">
                Loading more sessions...
              </div>
            )}
            {hasMore && (
              <div className="py-4 text-center">
                <DSButton
                  type="button"
                  onClick={loadMoreSessions}
                  disabled={isFetchingMore || loading}
                  dataTestId="companion-load-more-sessions"
                >
                  Load More Chat Sessions
                </DSButton>
              </div>
            )}
            {!hasMore && (
              <div className="py-4 text-center text-xs text-gray-300">
                You've reached the start of your Companion history.
              </div>
            )}
          </>
        ) : (
          <div
            className="w-full h-full flex justify-center items-center flex-col"
            data-testid="companion-empty-state"
          >
            <img
              src={OttoScribblesPawshake}
              className="w-3/4 max-w-96"
            />
            <div className="text-lg max-w-96 p-4 text-center text-white">
              Welcome to Companion! Click <strong>+ New Chat</strong>{" "}
              to get started, or{" "}
              <FontAwesomeIcon icon={faQuestionCircle} /> to learn
              more.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CompanionSessionHistoryItem = ({ item, onDeleted }) => {
  const [shouldShowDeleteModal, setShouldShowDeleteModal] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    setCompanionSession,
    getCompanionSession,
    setActiveTab,
    companionSession,
  } = useCompanion();

  const [deleteCompanionSession] = useMutation(
    DELETE_COMPANION_SESSION,
    {
      refetchQueries: [
        {
          query: GET_COMPANION_SESSIONS,
          variables: { limit: SESSIONS_TO_LOAD, offset: 0 },
        },
      ],
      awaitRefetchQueries: true,
    },
  );

  if (!item) {
    return null;
  }

  const selectItem = async () => {
    const companionSessionUuid = item.companionSessionUuid;
    const { data: companionSessionData } = await getCompanionSession({
      variables: {
        companionSessionUuid,
      },
    });
    mixpanel.track("Companion Session Selected", {
      companionSessionUuid,
    });
    setCompanionSession(companionSessionData?.companionSession);
    setActiveTab(AvailableCompanionTabs.chat);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShouldShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteCompanionSession({
        variables: {
          companionSessionUuid: item.companionSessionUuid,
        },
      });
      setIsDeleting(false);
      setShouldShowDeleteModal(false);
      alert("success", "Session deleted successfully");
      onDeleted?.();

      mixpanel.track("Companion Session Deleted", {
        companionSessionUuid: companionSession?.companionSessionUuid,
      });

      if (
        companionSession?.companionSessionUuid ===
        item.companionSessionUuid
      ) {
        setCompanionSession(null);
      }
    } catch (error) {
      setIsDeleting(false);
      setShouldShowDeleteModal(false);
      alert("error", error.message || "Failed to delete session");
    }
  };

  return (
    <>
      <div
        className="group rounded-xl p-2 my-2 bg-white hover:bg-indigo-100 transition-colors dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 cursor-pointer flex justify-between"
        onClick={selectItem}
        data-testid={`companion-chat-item-${item.companionSessionUuid}`}
      >
        <div className="flex flex-col">
          <b className="text-sm">
            {item.title || "New Companion Chat"}
          </b>
          <span className="text-xs text-gray-500">
            {moment(item.createdAt).format("MMM Do, h:mm A")}
          </span>
          <span className="flex gap-1">
            {item.companionsUsed.map((type) => {
              const emoji = AvailableCompanions[type]?.emoji;
              if (!emoji) {
                return null;
              }
              return (
                <span key={`companion-${type}`}>
                  <Twemoji emoji={emoji} />
                </span>
              );
            })}
          </span>
        </div>

        <div className="group-hover:opacity-100 opacity-0 transition-opacity flex items-center space-x-2">
          <button
            onClick={handleDeleteClick}
            className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 rounded-xl"
            title="Delete chat session"
            data-testid={`companion-delete-button-${item.companionSessionUuid}`}
          >
            <FontAwesomeIcon icon={faTrash} className="text-sm" />
          </button>
        </div>
      </div>

      <DeleteSessionModal
        shouldShow={shouldShowDeleteModal}
        hideModal={() => setShouldShowDeleteModal(false)}
        confirmAction={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </>
  );
};
